import { useMemo } from "react";
import type { SpreadsheetRangeAction } from "@components/spreadsheet/rangeAction";
import { ChatIcon } from "@icons";
import { useAddCellsToChat } from "../useAddCellsToChat";
import type { ChatDragItem } from "./chatDrag.types";
import { useChatDragAvailable } from "./chatDragContext";

/**
 * Builds a SpreadsheetGrid `rangeAction` that adds the selected cells to the
 * task chat on a click, or wherever they're dropped on a drag. Undefined
 * outside a task chat or with nothing selected, so the grid shows no button.
 */
export function useCellRangeChatAction(): SpreadsheetRangeAction | undefined {
    const isAvailable = useChatDragAvailable();
    const { pendingReference, addCellsToChat } = useAddCellsToChat();

    return useMemo(() => {
        if (!isAvailable || !pendingReference) {
            return undefined;
        }
        // No `onAdded`: once added, `pendingReference` turns null and hides the button.
        const item: ChatDragItem = {
            noun: "cell",
            count: pendingReference.cells.length,
            add: addCellsToChat,
        };
        return {
            label: "Add to chat",
            icon: <ChatIcon aria-hidden />,
            onClick: () => addCellsToChat(),
            dragData: item,
        };
    }, [isAvailable, pendingReference, addCellsToChat]);
}
